import { trackProps } from '../helpers/utils/analytics';
import { reportProblem } from '../helpers/utils/errorReporting';
import { onUpdateWaiting } from '../helpers/utils/updateOnReturn';
import { useScreenLayout } from '../helpers/utils/useScreenLayout';
import { useEffect, useState } from 'react';
import { Box } from './ui/box';
import { Button } from './ui/button';
import { HStack, VStack } from './ui/stack';
import { Text } from './ui/text';
import { ToastFrame } from './ui/toastFrame';
import {
  TOAST_CLOSE_CLEARANCE,
  TOAST_INSET,
  TOAST_SURFACE,
  TOAST_TEXT_GAP,
  ToastCloseButton,
  ToastIconBadge,
} from './ui/toastSurface';
import { useRegisterSW } from 'virtual:pwa-register/react';

const OFFLINE_TOAST_AUTO_DISMISS_MS = 4000;

const UpdateNotification = () => {
  const wide = useScreenLayout() !== 'phone';
  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [workerNeedsRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisterError(error: Error) {
      console.error('SW registration error', error);
      reportProblem('sw_register_failed', undefined, error);
    },
  });

  const [activatedUpdate, setActivatedUpdate] = useState(false);
  const needRefresh = workerNeedsRefresh || activatedUpdate;

  // A newer worker took over while the player was using the app: offer it
  // now. updateOnReturn.ts applies it on their next return otherwise, and
  // reloads straight into one that arrives before they touch anything.
  useEffect(() => onUpdateWaiting(() => setActivatedUpdate(true)), []);

  useEffect(() => {
    if (!offlineReady || needRefresh) return;
    const timer = setTimeout(
      () => setOfflineReady(false),
      OFFLINE_TOAST_AUTO_DISMISS_MS,
    );
    return () => clearTimeout(timer);
  }, [offlineReady, needRefresh, setOfflineReady]);

  const handleUpdate = () => {
    if (activatedUpdate) window.location.reload();
    else updateServiceWorker(true);
  };

  const handleDismiss = () => {
    setOfflineReady(false);
    setNeedRefresh(false);
    setActivatedUpdate(false);
  };

  const current = needRefresh ? 'update' : offlineReady ? 'offline' : null;
  // The notice keeps its content while it slides out.
  const [shown, setShown] = useState(current);
  if (current !== null && current !== shown) setShown(current);
  const isUpdate = shown === 'update';

  return (
    <ToastFrame show={current !== null} wide={wide}>
      {shown && (
          <Box
            {...TOAST_SURFACE}
            position="relative"
            zoom={wide ? 1.2 : 1}
            pl={TOAST_INSET}
            pr={TOAST_INSET}
            pt={isUpdate ? '8px' : '14px'}
            pb={TOAST_INSET}
          >
            <ToastCloseButton
              // The offline notice is not the update offer, so its X only
              // dismisses; `update_dismissed` stays on the update card.
              {...(isUpdate
                ? trackProps('update_dismissed', { method: 'close' })
                : {})}
              onClick={handleDismiss}
            />
            <VStack gap="var(--spades-spacing-ms)" align="stretch">
              <HStack
                gap="var(--spades-spacing-ms)"
                align="flex-start"
                pt={isUpdate ? '6px' : 0}
              >
                <ToastIconBadge success={!isUpdate} />
                <VStack
                  gap={TOAST_TEXT_GAP}
                  align="start"
                  flex="1"
                  minW="1px"
                  pr={TOAST_CLOSE_CLEARANCE}
                >
                  <Text
                    fontWeight="700"
                    fontSize="var(--spades-font-size-sm)"
                    lineHeight={1.25}
                  >
                    {isUpdate ? 'New version available' : 'Works offline'}
                  </Text>
                  <Text
                    fontSize="var(--spades-font-size-xs)"
                    fontWeight="400"
                    lineHeight={1.25}
                    color="var(--spades-muted)"
                  >
                    {isUpdate
                      ? 'A new version of Spades Calculator is ready. Update now to get the latest features and improvements.'
                      : "Keep score even without internet. You can also install it for offline use from your browser's menu."}
                  </Text>
                </VStack>
              </HStack>

              {isUpdate && (
                <HStack gap="var(--spades-spacing-sm)">
                  <Button
                    variant="primary"
                    h="44px"
                    minH="44px"
                    {...trackProps('update_requested')}
                    onClick={handleUpdate}
                    flex="1"
                  >
                    Update now
                  </Button>
                  <Button
                    variant="ghost"
                    h="44px"
                    minH="44px"
                    w="96px"
                    {...trackProps('update_dismissed', { method: 'later' })}
                    onClick={handleDismiss}
                  >
                    Later
                  </Button>
                </HStack>
              )}
            </VStack>
          </Box>
      )}
    </ToastFrame>
  );
};

export default UpdateNotification;
